/**
 * Shipping insurance sync: auto-add/remove/swap insurance line items;
 * toggle opt-in/out via cart attributes.
 *
 * Also owns the overnight surcharge line (window.overnightSurchargeConfig): one
 * serialized queue for both fee lines, so their cart mutations never interleave.
 */
(function () {
  // The cart page and drawer can both include this script. Share one queue and
  // one checkout listener across them.
  if (window.ShippingInsurance) return;

  const SYNC_SOURCE = 'shipping-insurance';
  // Budget for settling both fee lines after a Checkout click. Insurance plus the
  // surcharge can take 4–6 sequential cart requests (2.5s was routinely too short
  // on a slow connection); past this, checkout proceeds anyway.
  const CHECKOUT_FLUSH_TIMEOUT_MS = 10000;
  let syncPromise = null;
  let pendingSync = false;
  let debounceTimer = null;
  let cartUpdateUnsubscriber = null;
  let syncingUIActive = false;
  let toggleDelegationBound = false;
  let checkoutSubmitting = false;

  function getConfig() {
    return window.shippingInsuranceConfig || { enabled: false };
  }

  function getSurchargeConfig() {
    return window.overnightSurchargeConfig || { enabled: false };
  }

  // `active` = surcharge product configured. The sync keeps running while the
  // surcharge is switched off so a leftover surcharge line gets removed.
  function anyFeeEnabled() {
    const sc = getSurchargeConfig();
    return Boolean(getConfig().enabled || sc.enabled || sc.active);
  }

  // Pub/sub lives in pubsub.js + constants.js. Publishing should never be able to
  // throw out of a toggle/sync handler if those globals load late or are absent.
  function safePublish() {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: SYNC_SOURCE });
    }
  }

  // Detection lives in global.js (cartItemIsInsurance). Thin wrapper keeps the
  // local (item, config) call sites working and falls back if global.js is absent.
  function isInsuranceLine(item, config) {
    if (typeof cartItemIsInsurance === 'function') return cartItemIsInsurance(item);
    if (!item) return false;
    if (item.product_id === config.productId) return true;
    if (config.variantIds && config.variantIds.includes(item.variant_id)) return true;
    const props = item.properties || {};
    return Boolean(props[config.propertyKey] || props._shipping_insurance);
  }

  // Detection lives in global.js (cartItemIsSurcharge), with the same fallback.
  function isSurchargeLine(item) {
    if (typeof cartItemIsSurcharge === 'function') return cartItemIsSurcharge(item);
    const sc = getSurchargeConfig();
    if (!item || !(sc.enabled || sc.active)) return false;
    return (sc.productId && item.product_id === sc.productId) || Boolean((item.properties || {})[sc.propertyKey]);
  }

  function partitionCart(cart, config) {
    const insuranceLines = [];
    const surchargeLines = [];
    let merchandiseSubtotal = 0;
    let merchandiseCount = 0;

    (cart.items || []).forEach((item, index) => {
      if (isInsuranceLine(item, config)) {
        insuranceLines.push({ item, line: index + 1 });
      } else if (isSurchargeLine(item)) {
        // A fee, not merchandise: never counts toward tiers or the item count.
        surchargeLines.push({ item, line: index + 1 });
      } else {
        merchandiseCount += item.quantity;
        merchandiseSubtotal += item.final_line_price;
      }
    });

    return { insuranceLines, surchargeLines, merchandiseSubtotal, merchandiseCount };
  }

  function resolveTier(subtotalCents, config) {
    const tiers = (config.tiers || []).slice().sort((a, b) => b.minCents - a.minCents);
    for (const tier of tiers) {
      if (subtotalCents >= tier.minCents) return tier;
    }
    return null;
  }

  /**
   * Cheapest variant priced at or above `priceCents`, falling back to the dearest
   * one when the target overshoots the ladder — a cart beyond the top variant is
   * charged that ceiling rather than losing its cover entirely.
   */
  function resolveVariantForPrice(config, priceCents) {
    let above = null;
    let dearest = null;

    (config.priceVariants || []).forEach((pair) => {
      if (pair[0] >= priceCents && (above === null || pair[0] < above[0])) above = pair;
      if (dearest === null || pair[0] > dearest[0]) dearest = pair;
    });

    const chosen = above || dearest;
    return chosen ? chosen[1] : null;
  }

  /**
   * A percentage tier has no variant of its own: the fee is a percentage of the
   * basis, rounded to a whole number of ladder steps so it lands on a real variant
   * price. Keep in sync with the tier block in snippets/shipping-insurance-cart-meta.liquid.
   */
  function resolveTierVariantId(config, basisCents) {
    const tier = resolveTier(basisCents, config);
    if (!tier) return null;

    if (tier.percent > 0) {
      const step = config.percentStepCents > 0 ? config.percentStepCents : 10;
      return resolveVariantForPrice(config, Math.round((basisCents * tier.percent) / 100 / step) * step);
    }

    return tier.variantId || null;
  }

  function isOptedOut(cart) {
    const attrs = cart.attributes || {};
    return attrs.shipping_insurance_opt_out === 'true';
  }

  function getTierBasisCents(cart, config, partition) {
    if (config.subtotalBasis === 'total_price') {
      let total = cart.total_price || 0;
      partition.insuranceLines.concat(partition.surchargeLines).forEach(({ item }) => {
        total -= item.final_line_price;
      });
      return Math.max(0, total);
    }
    return partition.merchandiseSubtotal;
  }

  function applySyncingUI() {
    document.body.toggleAttribute('data-insurance-syncing', syncingUIActive);
    document.querySelectorAll('[data-shipping-insurance]').forEach((el) => {
      el.toggleAttribute('data-syncing', syncingUIActive);
      el.setAttribute('aria-busy', syncingUIActive ? 'true' : 'false');
      const toggle = el.querySelector('[data-insurance-toggle]');
      if (toggle) {
        toggle.disabled = syncingUIActive;
        toggle.setAttribute('aria-busy', syncingUIActive ? 'true' : 'false');
      }
    });
  }

  function setBodySyncing(active) {
    syncingUIActive = active;
    applySyncingUI();
  }

  function showError(message) {
    document.querySelectorAll('[data-insurance-error]').forEach((el) => {
      el.textContent = message || '';
      el.classList.toggle('visually-hidden', !message);
    });
  }

  function getDrawerSections() {
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  function getPageSections() {
    const mainCartItems = document.getElementById('main-cart-items');
    const mainCartFooter = document.getElementById('main-cart-footer');
    const sections = ['cart-icon-bubble'];
    if (mainCartItems && mainCartItems.dataset.id) {
      sections.push(mainCartItems.dataset.id);
    }
    if (mainCartFooter && mainCartFooter.dataset.id) {
      sections.push(mainCartFooter.dataset.id);
    }
    return sections;
  }

  function getSectionsToRender() {
    const onCartPage = document.getElementById('main-cart-items');
    return onCartPage ? getPageSections() : getDrawerSections();
  }

  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const node = doc.querySelector(selector);
    return node ? node.innerHTML : '';
  }

  function isCartVisuallyEmpty(cartState) {
    const config = getConfig();
    if (!anyFeeEnabled()) return cartState.item_count === 0;
    const partition = partitionCart(cartState, config);
    return partition.merchandiseCount === 0;
  }

  function applySectionUpdates(parsedState) {
    if (!parsedState.sections) return;

    const onCartPage = document.getElementById('main-cart-items');

    if (onCartPage) {
      const cartItemsEl = document.querySelector('cart-items');
      const sectionMap = [
        { id: 'main-cart-items', selector: '.js-contents' },
        { id: 'main-cart-footer', selector: '.cart__blocks .js-contents' },
        { id: 'cart-icon-bubble', selector: '.shopify-section' },
      ];

      sectionMap.forEach(({ id, selector }) => {
        const sectionKey =
          id === 'main-cart-items'
            ? onCartPage.dataset.id
            : id === 'main-cart-footer'
              ? document.getElementById('main-cart-footer')?.dataset.id
              : 'cart-icon-bubble';
        const html = parsedState.sections[sectionKey];
        if (!html) return;
        const target = document.getElementById(id);
        if (!target) return;
        const replaceEl = target.querySelector(selector) || target;
        replaceEl.innerHTML = getSectionInnerHTML(html, selector);
      });

      if (typeof parsedState.item_count === 'number' && cartItemsEl) {
        const visibleEmpty = isCartVisuallyEmpty(parsedState);
        cartItemsEl.classList.toggle('is-empty', visibleEmpty);
        const footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.toggle('is-empty', visibleEmpty);
      }
    } else {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      cartDrawer.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement || !parsedState.sections[section.id]) return;
        sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      });

      if (typeof parsedState.item_count === 'number') {
        cartDrawer.classList.toggle('is-empty', isCartVisuallyEmpty(parsedState));
      }

      setTimeout(() => {
        if (typeof cartDrawer.bindDrawerActions === 'function') {
          cartDrawer.bindDrawerActions();
        }
      });
    }

    bindToggleHandlers();
    if (syncingUIActive) applySyncingUI();
    reconcileToggleState(parsedState);
  }

  async function fetchCart() {
    const res = await fetch(`${window.routes.cart_url}.js`);
    if (!res.ok) throw new Error('cart-fetch-failed');
    return res.json();
  }

  async function reconcileToggleState(cartState) {
    const config = getConfig();
    if (!config.enabled) return;

    try {
      // Only reuse cartState if it is a full cart response (has `attributes`).
      // /cart/add.js returns only the added item(s) — using it causes partitionCart
      // to see zero merchandise items and wrongly uncheck the toggle even though the
      // subtotal already reflects insurance being added.
      const isFullCart = cartState && Array.isArray(cartState.items) && 'attributes' in cartState;
      const cart = isFullCart ? cartState : await fetchCart();
      const partition = partitionCart(cart, config);
      const shouldBeChecked = partition.merchandiseCount > 0 && !isOptedOut(cart);

      document.querySelectorAll('[data-shipping-insurance]').forEach((el) => {
        el.setAttribute('data-insurance-enabled', shouldBeChecked ? 'true' : 'false');
        const toggle = el.querySelector('[data-insurance-toggle]');
        if (!toggle) return;
        toggle.checked = shouldBeChecked;
        toggle.setAttribute('aria-checked', shouldBeChecked ? 'true' : 'false');
      });
    } catch (err) {
      console.error('Shipping insurance toggle reconcile failed:', err);
    }
  }

  async function cartAdd(variantId, propertyKey) {
    const sections = getSectionsToRender();
    const body = {
      items: [
        {
          id: variantId,
          quantity: 1,
          properties: { [propertyKey]: 'true' },
        },
      ],
      sections,
      sections_url: window.location.pathname,
    };
    const res = await fetch(`${window.routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(body),
    });
    return res.json();
  }

  /**
   * Drops a line addressed by its line item key rather than its position.
   *
   * Keys stay valid across cart mutations; line numbers do not. A tier swap adds
   * before it removes, which renumbers the lines, so the removal must be keyed or
   * it deletes whichever line has moved into that slot.
   *
   * `withSections` bundles the re-render, so the shopper sees a single repaint.
   */
  async function cartRemoveByKey(key, withSections) {
    const payload = { id: key, quantity: 0 };
    if (withSections) {
      payload.sections = getSectionsToRender();
      payload.sections_url = window.location.pathname;
    }
    const res = await fetch(`${window.routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(payload),
    });
    return JSON.parse(await res.text());
  }

  /**
   * Moves the insurance line onto a different tier.
   *
   * Shopify's cart cannot repoint an existing line at another variant: a
   * /cart/change.js call carrying both `line` and a different `id` fails, which
   * is what used to leave a raised fee showing the old tier's price until the
   * shopper toggled the widget off and on again. Add the new tier first, then
   * drop the old line by key — the same workaround CartItems.upgradeLine uses.
   */
  async function swapInsuranceTier(existingItem, targetVariantId, config) {
    const addBody = {
      items: [
        {
          id: Number(targetVariantId),
          quantity: 1,
          properties: { [config.propertyKey]: 'true' },
        },
      ],
    };
    const added = await fetch(`${window.routes.cart_add_url}`, {
      ...fetchConfig(),
      body: JSON.stringify(addBody),
    }).then((r) => r.json());

    if (added && added.status) {
      showError(added.description || config.strings?.error);
      return null;
    }

    return cartRemoveByKey(existingItem.key, true);
  }

  async function cartUpdateAttributes(attributes) {
    const sections = getSectionsToRender();
    const res = await fetch(`${window.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        attributes,
        sections,
        sections_url: window.location.pathname,
      }),
    });
    return JSON.parse(await res.text());
  }

  async function runSync() {
    // Insurance first: its basis excludes the surcharge, so adding or dropping
    // the surcharge afterwards can never move the insurance tier. The surcharge
    // basis may include insurance, so it must see the settled insurance line.
    const insuranceResult = await runInsuranceSync();
    const surchargeResult = await runSurchargeSync();
    // The later result carries the newer sections; either is null on no change.
    return surchargeResult || insuranceResult;
  }

  async function runInsuranceSync() {
    const config = getConfig();
    if (!config.enabled || !config.productId || !config.tiers || config.tiers.length === 0) {
      return null;
    }

    const cart = await fetchCart();
    let partition = partitionCart(cart, config);
    const optedOut = isOptedOut(cart);
    let targetVariantId = resolveTierVariantId(config, getTierBasisCents(cart, config, partition));

    showError('');

    if (partition.merchandiseCount === 0 || optedOut) {
      let lastResult = null;
      for (let i = partition.insuranceLines.length - 1; i >= 0; i--) {
        lastResult = await cartRemoveByKey(partition.insuranceLines[i].item.key, true);
      }
      // Null when there was no insurance line: nothing changed, no re-render needed.
      return lastResult;
    }

    if (!targetVariantId) {
      showError(config.strings?.error || 'Insurance unavailable.');
      return null;
    }

    // Collapse duplicates by key, then re-read and act on whatever survived.
    let collapseResult = null;
    if (partition.insuranceLines.length > 1) {
      for (let i = partition.insuranceLines.length - 1; i > 0; i--) {
        // Bundle the re-render on the final removal so the collapsed list still
        // repaints when the surviving line already sits on the right tier.
        collapseResult = await cartRemoveByKey(partition.insuranceLines[i].item.key, i === 1);
      }
      // Re-read: dropping insurance lines moves the basis when it is cart-total
      // based, which can select a different tier than the pre-collapse cart did.
      const refreshed = await fetchCart();
      partition = partitionCart(refreshed, config);
      targetVariantId = resolveTierVariantId(config, getTierBasisCents(refreshed, config, partition));
      if (!targetVariantId) return collapseResult;
    }

    if (partition.insuranceLines.length === 0) {
      const result = await cartAdd(targetVariantId, config.propertyKey);
      if (result.status) {
        showError(result.description || config.strings?.error);
        return collapseResult;
      }
      return result;
    }

    const { item } = partition.insuranceLines[0];
    if (String(item.variant_id) !== String(targetVariantId)) {
      return swapInsuranceTier(item, targetVariantId, config);
    }

    // Already on the correct tier. Non-null only if a collapse needs painting.
    return collapseResult;
  }

  async function cartChangeByKey(key, quantity) {
    const res = await fetch(`${window.routes.cart_change_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        id: key,
        quantity,
        sections: getSectionsToRender(),
        sections_url: window.location.pathname,
      }),
    });
    return JSON.parse(await res.text());
  }

  /**
   * Whether the cart holds a fish product. /cart.js carries no collection or tag
   * data, so sections/overnight-surcharge-state.liquid answers it server-side.
   * Asked through the cart route, which is never served from cache.
   */
  async function cartHasFish(sc) {
    // Bounded, so a hung request fails into the "leave the cart as is" path
    // instead of wedging the shared sync queue for the rest of the page's life.
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    const abortTimer = controller ? setTimeout(() => controller.abort(), 8000) : null;
    let res;
    try {
      res = await fetch(`${window.routes.cart_url}?sections=${encodeURIComponent(sc.stateSection)}`, {
        signal: controller ? controller.signal : undefined,
      });
    } finally {
      if (abortTimer) clearTimeout(abortTimer);
    }
    if (!res.ok) throw new Error('surcharge-state-fetch-failed');
    const sections = await res.json();
    const html = sections && sections[sc.stateSection];
    if (!html) throw new Error('surcharge-state-missing');
    const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-overnight-surcharge-state]');
    if (!node) throw new Error('surcharge-state-missing');
    return Boolean(JSON.parse(node.textContent).hasFish);
  }

  /**
   * Keeps exactly one surcharge line, at quantity 1, in the cart while the basis
   * meets the threshold and the cart holds fish, and none otherwise. The shopper
   * has no control over the line; every cart change re-runs this.
   */
  async function runSurchargeSync() {
    const sc = getSurchargeConfig();
    if (!sc.enabled && !sc.active) return null;

    const cart = await fetchCart();
    const partition = partitionCart(cart, getConfig());
    const lines = partition.surchargeLines;

    // Switched off (or misconfigured): only clean up a line left from before.
    if (!sc.enabled || !sc.variantId) {
      let lastResult = null;
      for (let i = lines.length - 1; i >= 0; i--) {
        lastResult = await cartRemoveByKey(lines[i].item.key, i === 0);
      }
      return lastResult;
    }

    let needed = false;
    if (partition.merchandiseCount > 0) {
      let basis = partition.merchandiseSubtotal;
      if (sc.includeInsurance) {
        partition.insuranceLines.forEach(({ item }) => {
          basis += item.final_line_price;
        });
      }
      if (basis >= sc.thresholdCents) {
        try {
          needed = await cartHasFish(sc);
        } catch (err) {
          // Fish state unknown: leave the cart as it is rather than flapping the
          // line on a transient error. The next cart change retries.
          console.error('Overnight surcharge state fetch failed:', err);
          return null;
        }
      }
    }

    if (!needed) {
      let lastResult = null;
      for (let i = lines.length - 1; i >= 0; i--) {
        // Bundle the re-render on the final removal only.
        lastResult = await cartRemoveByKey(lines[i].item.key, i === 0);
      }
      return lastResult;
    }

    // Keep one line already on the configured variant; drop every other one.
    const kept = lines.find(({ item }) => String(item.variant_id) === String(sc.variantId));
    const extras = lines.filter((line) => line !== kept);
    let lastResult = null;
    for (let i = extras.length - 1; i >= 0; i--) {
      lastResult = await cartRemoveByKey(extras[i].item.key, Boolean(kept) && i === 0);
    }

    if (!kept) {
      const added = await cartAdd(Number(sc.variantId), sc.propertyKey);
      if (added && added.status) {
        console.error('Overnight surcharge add failed:', added.description || added.message);
        return lastResult;
      }
      return added;
    }

    if (kept.item.quantity !== 1) return cartChangeByKey(kept.item.key, 1);
    return lastResult;
  }

  function processSyncQueue(operation = runSync) {
    if (syncPromise) {
      pendingSync = true;
      return syncPromise;
    }

    setBodySyncing(true);

    // All callers await the same drain, including any updates queued while a
    // request is in flight. Never poll an immediately resolved async function:
    // that starves network callbacks, rendering, and even the checkout timeout.
    syncPromise = Promise.resolve().then(async () => {
      do {
        pendingSync = false;
        try {
          const result = await operation();
          if (result && result.sections) {
            applySectionUpdates(result);
            safePublish();
          }
        } catch (err) {
          console.error('Shipping insurance sync failed:', err);
          showError(getConfig().strings?.error || 'Unable to update shipping insurance.');
        }
        operation = runSync;
      } while (pendingSync);
    }).finally(() => {
      syncPromise = null;
      setBodySyncing(false);
    });

    return syncPromise;
  }

  function scheduleSync(immediate) {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = null;
    if (immediate) {
      processSyncQueue();
      return;
    }
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      processSyncQueue();
    }, typeof ON_CHANGE_DEBOUNCE_TIMER !== 'undefined' ? ON_CHANGE_DEBOUNCE_TIMER : 300);
  }

  async function handleToggle(event) {
    const toggle = event.target.closest('[data-insurance-toggle]');
    if (!toggle || toggle.disabled || syncingUIActive) return;

    const config = getConfig();
    if (!config.enabled) return;

    const enabled = toggle.checked;
    showError('');

    // Own the queue before saving the preference, so checkout also waits for
    // the attribute request and cannot sync using the previous opt-in state.
    await processSyncQueue(async () => {
      try {
        const attributes = {};
        attributes[config.optOutAttribute] = enabled ? '' : 'true';
        const result = await cartUpdateAttributes(attributes);
        return (await runSync()) || result;
      } catch (err) {
        console.error('Shipping insurance toggle failed:', err);
        document.querySelectorAll('[data-insurance-toggle]').forEach((input) => {
          input.checked = !enabled;
          input.setAttribute('aria-checked', !enabled ? 'true' : 'false');
        });
        showError(config.strings?.error);
        return null;
      }
    });
  }

  // Bind via event delegation on the document rather than per-element. Dawn's
  // cart-drawer renderContents() blows away and rebuilds #CartDrawer innerHTML on
  // every cart mutation, which orphaned any listener attached directly to the
  // toggle (the toggle then flipped visually via CSS :checked but fired no cart
  // logic). A single delegated listener survives every re-render.
  function bindToggleHandlers() {
    if (toggleDelegationBound) return;
    toggleDelegationBound = true;
    document.addEventListener('change', (event) => {
      const toggle = event.target && event.target.closest
        ? event.target.closest('[data-insurance-toggle]')
        : null;
      if (toggle) handleToggle(event);
    });
  }

  function interceptCheckout() {
    document.addEventListener(
      'click',
      async (event) => {
        const checkoutBtn = event.target.closest('#CartDrawer-Checkout, #checkout');
        if (!checkoutBtn || checkoutBtn.disabled) return;

        if (!anyFeeEnabled()) return;

        event.preventDefault();
        event.stopPropagation();

        // Guard against double-submit: the flush below is async, so without this a
        // rapid second click would fire a second submit. Keep the control enabled
        // until the synchronized cart has been submitted.
        if (checkoutSubmitting) return;
        checkoutSubmitting = true;

        // Keep the cart-page and drawer checkout flows separate. Both controls
        // exist in the DOM on the cart page, and the insurance flush can replace
        // either control before it completes, so retain their IDs rather than
        // resolving whichever checkout button happens to appear first later.
        const checkoutButtonId = checkoutBtn.id;
        const checkoutFormId = checkoutBtn.getAttribute('form') || checkoutBtn.form?.id;

        // Submit the form associated with the checkout control, then disable the
        // control to reflect the in-flight state. Returns true if submitted.
        //
        // Re-resolves the button/form live rather than reusing `checkoutBtn`: while
        // we're awaiting the flush below, a cart re-render (e.g. the shopper
        // unchecking insurance right before clicking Checkout) replaces
        // #CartDrawer's innerHTML wholesale, detaching the node captured at click
        // time. Passing a detached submitter to form.requestSubmit() throws
        // ("not owned by this form"); that used to escape uncaught and leave
        // checkoutSubmitting stuck at true forever, permanently dead-ending the
        // button. Every step here is wrapped so it can never throw out to the
        // caller — the caller's finally always clears the flag.
        const submitCheckout = () => {
          const liveBtn = document.getElementById(checkoutButtonId) || checkoutBtn;
          const form =
            (checkoutFormId && document.getElementById(checkoutFormId)) ||
            liveBtn.form ||
            liveBtn.closest('form');
          if (!form) return false;

          try {
            // Keep an explicit checkout field in the form. This preserves the
            // Shopify checkout intent even when a section re-render detached the
            // clicked button or a stale/disabled button cannot be a successful
            // submitter; without it, POST /cart only reloads the cart page.
            let checkoutInput = form.querySelector('input[data-insurance-checkout]');
            if (!checkoutInput) {
              checkoutInput = document.createElement('input');
              checkoutInput.type = 'hidden';
              checkoutInput.name = 'checkout';
              checkoutInput.value = 'Checkout';
              checkoutInput.setAttribute('data-insurance-checkout', '');
              form.appendChild(checkoutInput);
            }

            // Quantities were already applied by line item key above. The form's
            // updates[] list is positional and has no entry for the hidden fee
            // lines, so posting it could only skip or misplace quantities.
            document.querySelectorAll('[name="updates[]"]').forEach((input) => {
              if (input.form !== form) return;
              input.removeAttribute('name');
              input.setAttribute('data-fee-checkout-unnamed', '');
            });

            if (typeof form.requestSubmit === 'function') {
              form.requestSubmit();
            } else {
              form.submit();
            }
          } catch (err) {
            console.error('Checkout submit failed, falling back to plain submit:', err);
            try {
              form.submit();
            } catch (err2) {
              console.error('Checkout fallback submit failed:', err2);
              return false;
            }
          }

          // Disable only after initiating submit so validation and submission see an
          // enabled checkout control; the hidden checkout field preserves intent.
          liveBtn.disabled = true;
          liveBtn.setAttribute('data-fee-checkout-disabled', '');
          return true;
        };

        // Visible "working" state while the fees settle. Lives on <body> so it
        // survives the drawer re-renders the sync triggers; the control itself
        // stays enabled so it is never disabled without a reason.
        document.body.setAttribute('data-checkout-pending', '');

        let submitted = false;
        try {
          // One budget for the whole hand-off. Never let a stalled cart request
          // block checkout indefinitely: once it runs out, submit regardless.
          await withTimeout(prepareCartForCheckout(checkoutFormId), CHECKOUT_FLUSH_TIMEOUT_MS);
        } catch (err) {
          console.error('Checkout fee sync failed:', err);
        } finally {
          submitted = submitCheckout();
          if (!submitted) {
            checkoutSubmitting = false;
            document.body.removeAttribute('data-checkout-pending');
          }
        }
      },
      true
    );

    // Coming Back from checkout restores this page from the back/forward cache
    // exactly as it was left: checkout disabled and the double-submit guard set,
    // which would leave a dead button. Reset both and re-check the fees, since
    // the cart may have changed in checkout or another tab.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      checkoutSubmitting = false;
      document.body.removeAttribute('data-checkout-pending');
      document.querySelectorAll('[data-fee-checkout-disabled]').forEach((btn) => {
        btn.disabled = false;
        btn.removeAttribute('data-fee-checkout-disabled');
      });
      document.querySelectorAll('[data-fee-checkout-unnamed]').forEach((input) => {
        input.name = 'updates[]';
        input.removeAttribute('data-fee-checkout-unnamed');
      });
      scheduleSync(true);
    });
  }

  // In-flight cart mutations from anywhere on the page (Dawn's cart.js, product
  // forms, apps). Checkout must not snapshot the cart while one is still landing:
  // the fee sync would compute from the cart as it was before that change.
  let cartMutationsInFlight = 0;
  let cartIdleWaiters = [];
  const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  function settleCartIdleWaiters() {
    if (cartMutationsInFlight > 0) return;
    const waiters = cartIdleWaiters;
    cartIdleWaiters = [];
    waiters.forEach((resolve) => resolve());
  }

  function trackCartMutations() {
    if (window.__feeCartFetchTracked || typeof window.fetch !== 'function') return;
    window.__feeCartFetchTracked = true;
    const originalFetch = window.fetch;
    window.fetch = function (input, init) {
      const url = typeof input === 'string' ? input : (input && input.url) || '';
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      if (method !== 'POST' || !CART_MUTATION_RE.test(url)) {
        return originalFetch.apply(this, arguments);
      }
      cartMutationsInFlight += 1;
      const done = () => {
        cartMutationsInFlight = Math.max(0, cartMutationsInFlight - 1);
        settleCartIdleWaiters();
      };
      const request = originalFetch.apply(this, arguments);
      request.then(done, done);
      return request;
    };
  }

  function waitForCartIdle() {
    if (cartMutationsInFlight === 0) return Promise.resolve();
    return new Promise((resolve) => cartIdleWaiters.push(resolve));
  }

  /**
   * Quantities typed into the cart form that have not reached the cart yet:
   * Dawn debounces quantity changes before sending them, so a fast Checkout
   * click can beat the request. Applied by line item key, which also means the
   * form's positional updates[] list is never what decides a quantity.
   */
  async function applyPendingFormQuantities(formId) {
    const form = formId && document.getElementById(formId);
    if (!form) return;
    const inputs = Array.from(document.querySelectorAll('.quantity__input[data-index]')).filter(
      (input) => input.form === form || form.contains(input)
    );
    if (inputs.length === 0) return;

    const cart = await fetchCart();
    const updates = {};
    let changed = false;
    inputs.forEach((input) => {
      const item = cart.items[Number(input.dataset.index) - 1];
      const quantity = parseInt(input.value, 10);
      if (!item || Number.isNaN(quantity) || quantity < 0) return;
      if (String(item.variant_id) !== String(input.dataset.quantityVariantId)) return;
      if (item.quantity === quantity) return;
      updates[item.key] = quantity;
      changed = true;
    });
    if (!changed) return;

    await fetch(`${window.routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({ updates }),
    });
  }

  async function prepareCartForCheckout(formId) {
    await waitForCartIdle();
    try {
      await applyPendingFormQuantities(formId);
    } catch (err) {
      console.error('Applying pending quantities before checkout failed:', err);
    }
    await waitForCartIdle();
    await flush();
  }

  async function withTimeout(promise, ms) {
    let timeout;
    try {
      await Promise.race([promise, new Promise((resolve) => { timeout = setTimeout(resolve, ms); })]);
    } finally {
      clearTimeout(timeout);
    }
  }

  function flush() {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    return processSyncQueue();
  }

  function init() {
    if (!anyFeeEnabled()) return;

    trackCartMutations();
    bindToggleHandlers();
    interceptCheckout();

    // Guard against load-order surprises: these globals come from global.js / pubsub.js.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
      console.warn('Shipping insurance: pub/sub globals unavailable; cart sync disabled.');
      return;
    }

    if (cartUpdateUnsubscriber) cartUpdateUnsubscriber();
    cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!event || event.source === SYNC_SOURCE) return;
      if (syncingUIActive) applySyncingUI();
      reconcileToggleState();
      scheduleSync(event.source === 'product-form');
    });

    const cartDrawer = document.querySelector('cart-drawer');
    const onCartPage = document.getElementById('main-cart-items');
    if ((cartDrawer && !cartDrawer.classList.contains('is-empty')) || onCartPage) {
      scheduleSync(true);
    }
  }

  window.ShippingInsurance = {
    sync: (immediate) => scheduleSync(immediate !== false),
    flush,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
